/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ProxyMessageHandlers } from "./handlers.js";
import { PROXY_EXTERNAL_MESSAGE_TYPE, ProxyExternalMessageSchema, ProxyBackgroundMessageSchema } from "./transport.js";
import { z } from "zod";

export async function dispatchProxyMessage<
	H extends keyof ProxyMessageHandlers,
	P extends Parameters<ProxyMessageHandlers[H]>[0],
	R extends Awaited<ReturnType<ProxyMessageHandlers[H]>>,
>(handler: H, ...args: [params: P, timeout?: number]): Promise<R>;

export async function dispatchProxyMessage<M extends Record<string, any>>(
	message: M,
	...args: [timeout?: number]
): Promise<any>;

export async function dispatchProxyMessage<
	M extends Record<string, any>,
	H extends keyof ProxyMessageHandlers,
	FnParams extends M | H,
	P extends Parameters<ProxyMessageHandlers[H]>[0],
	R extends Awaited<ReturnType<ProxyMessageHandlers[H]>>,
	FnReturn extends FnParams extends H ? R : any,
>(handlerOrMessage: FnParams, ...args: any[]): Promise<FnReturn> {
	const { promise, resolve, reject } = Promise.withResolvers<any>();

	const id = crypto.randomUUID();
	const responseHandler = (event: MessageEvent) => {
		if (event.source !== window) {
			return;
		}
		void ProxyBackgroundMessageSchema.refine((data) => data.id === id)
			.safeParseAsync(event.data)
			.then(({ success, data }) => {
				if (success) {
					window.removeEventListener("message", responseHandler);

					if (data.success) {
						resolve(data.result);
					} else {
						reject(
							new Error(
								JSON.stringify(`[ProxyMessage] got an error response for request ${id} ${JSON.stringify(data.error)}`),
							),
						);
					}
				}
			});
	};

	window.addEventListener("message", responseHandler);

	if (typeof handlerOrMessage === "string") {
		const [params, timeout] = args as [params: P, timeout?: number];
		window.postMessage(
			{
				id,
				handler: handlerOrMessage,
				params,
				type: PROXY_EXTERNAL_MESSAGE_TYPE,
			} satisfies z.infer<typeof ProxyExternalMessageSchema>,
			window.origin,
		);
		if (timeout !== undefined) {
			setTimeout(() => {
				reject(`Message ${id} to ${handlerOrMessage} timed out`);
			}, timeout);
		}
	} else {
		const [timeout] = args as [timeout?: number];
		window.postMessage(
			{
				id,
				message: handlerOrMessage,
				type: PROXY_EXTERNAL_MESSAGE_TYPE,
			} satisfies z.infer<typeof ProxyExternalMessageSchema>,
			window.origin,
		);
		if (timeout !== undefined) {
			setTimeout(() => {
				reject(`Message ${id} timed out`);
			}, timeout);
		}
	}

	return promise;
}

export const waitForProxyMessageChannelReady = (timeoutMs: number = 60000) => {
	return new Promise<void>((res, rej) => {
		const timeoutId = setTimeout(() => {
			rej(`Proxy message channel not ready after ${timeoutMs}ms`);
		}, timeoutMs);

		const resolveOnPong = () => {
			dispatchProxyMessage(`ping`, undefined, 100)
				.then(() => {
					clearTimeout(timeoutId);
					res();
				})
				.catch(() => {
					setTimeout(resolveOnPong, 100);
				});
		};

		resolveOnPong();
	});
};
